import React from 'react';
import { RouteAccess } from 'vrlcm-gui-types';
import { l10n } from '../../../i18n/i18nUtil';

export const BASE_ROUTE = '/lcm/lcops';
export const LOCKER = `/lcm/locker`;
export const CLOUD = '/lcm/cloud';

export const ROUTES = {
  DASHBOARD: `${BASE_ROUTE}/dashboard`,
  DATACENTERS: `${BASE_ROUTE}/datacenters`,
  ENVIRONMENTS: `${BASE_ROUTE}/environments`,
  REQUESTS: `${BASE_ROUTE}/requests`,
  SETTINGS: `${BASE_ROUTE}/settings`,
  ENVIRONMENT_WORKFLOW: `${BASE_ROUTE}/environment-workflow`,
  UPGRADE_PRODUCT: `${BASE_ROUTE}/environments/upgrade-product`,
  CONFIG_DRIFT: `${BASE_ROUTE}/environments/config-drift`,
  ADD_DATA_SOURCES: `${BASE_ROUTE}/environments/add-data-sources`,
  UPGRADE_PLANNER: `${BASE_ROUTE}/environments/upgrade-planner`,
  EXECUTE_SCRIPTS: `${BASE_ROUTE}/environments/execute-script`,
  PAGE_UNKNOWN: `${BASE_ROUTE}/page-unknown`
};

// If entry not present corresponding to role, default route is Dashboard
export const DEFAULT_ROLE_ROUTES = {
  LOCKER_CERTIFICATE_ADMIN: ROUTES.ENVIRONMENTS
};

const Dashboard = React.lazy(() => import('../components/dashboard/Dashboard'));
const Datacenter = React.lazy(() =>
  import('../components/datacenter/Datacenter')
);
const EnvironmentDetails = React.lazy(() =>
  import('../components/environments/EnvironmentDetails')
);
const Environments = React.lazy(() =>
  import('../components/environments/Environments')
);
const RequestStatus = React.lazy(() =>
  import('../components/requests/details/RequestStatus')
);
const Requests = React.lazy(() => import('../components/requests/Requests'));
const SettingsRoot = React.lazy(() =>
  import('../components/settings/SettingsRoot')
);
const EnvironmentWorkflow = React.lazy(() =>
  import('../components/workflows/environment-workflow/EnvironmentWorkflow')
);
const UpgradeWorkflow = React.lazy(() =>
  import('../components/workflows/upgrade-workflow/UpgradeWorkflow')
);
const ConfigDrift = React.lazy(() =>
  import('../components/config-drift/ConfigDrift')
);
const AddDataSources = React.lazy(() =>
  import('../components/environments/actions/add-data-sources/AddDataSources')
);

const AutoDiscoverVms = React.lazy(() =>
  import('../components/datacenter/auto-discover-vms/AutoDiscoverVm')
);

const UpgradePlanner = React.lazy(() =>
  import('../components/environments/EnvironmentsUpgradePlanner')
);

const ExecuteScripts = React.lazy(() =>
  import('../components/environments/EnvironmentsExecuteScripts')
);

/* This goes in a switch so first match scenario is being considered */
export const ROUTES_COMPONENT_MAP = {
  [ROUTES.DASHBOARD]: Dashboard,
  [`${ROUTES.DATACENTERS}/:id/auto-discover-vms`]: AutoDiscoverVms,
  [ROUTES.DATACENTERS]: Datacenter,
  [`${ROUTES.ENVIRONMENT_WORKFLOW}/:requestId?`]: EnvironmentWorkflow,
  [`${ROUTES.UPGRADE_PRODUCT}/:requestId`]: UpgradeWorkflow,
  [ROUTES.CONFIG_DRIFT]: ConfigDrift,
  [ROUTES.ADD_DATA_SOURCES]: AddDataSources,
  [ROUTES.UPGRADE_PLANNER]: UpgradePlanner,
  [ROUTES.EXECUTE_SCRIPTS]: ExecuteScripts,
  [`${ROUTES.ENVIRONMENTS}/:id`]: EnvironmentDetails,
  [ROUTES.ENVIRONMENTS]: Environments,
  [`${ROUTES.REQUESTS}/:id`]: RequestStatus,
  [ROUTES.REQUESTS]: Requests,
  [`${ROUTES.SETTINGS}/:section?`]: SettingsRoot
};

export const getAuthorizedRoutes = (
  roles: string[],
  routeAccess: RouteAccess
) => {
  const routesAuthorities = Object.entries(routeAccess);
  const authorizedRoutes = routesAuthorities.reduce<
    typeof ROUTES_COMPONENT_MAP
  >((acc, [route, authorities]) => {
    if (
      (typeof authorities === 'string' && authorities === '*') ||
      (Array.isArray(authorities) &&
        authorities.some(a => roles.some(r => r === a)))
    ) {
      if (ROUTES_COMPONENT_MAP[route]) {
        acc[route] = ROUTES_COMPONENT_MAP[route];
      }
    }
    return acc;
  }, {});
  return authorizedRoutes;
};

export const VERTICAL_NAV_ITEMS = {
  [ROUTES.DASHBOARD]: {
    title: l10n('lcops.dashboard.title'),
    icon: 'home'
  },
  [ROUTES.ENVIRONMENT_WORKFLOW]: {
    title: l10n('lcops.dashboard.createEnvironment'),
    icon: 'vm'
  },
  [ROUTES.DATACENTERS]: {
    title: l10n('lcops.dataCenters.title'),
    icon: 'building'
  },
  [ROUTES.ENVIRONMENTS]: {
    title: l10n('environments'),
    icon: 'cluster'
  },
  [ROUTES.REQUESTS]: {
    title: l10n('lcops.requests.title'),
    icon: 'list'
  },
  [ROUTES.SETTINGS]: {
    title: l10n('settings'),
    icon: 'settings'
  }
};

export const getAuthorizedNavItems = (
  roles: string[],
  routeAccess: RouteAccess
) => {
  const authorizedRoutes = getAuthorizedRoutes(roles, routeAccess);
  const routeSet = new Set<string>();
  Object.keys(authorizedRoutes).forEach(r => {
    const routeSplit = r.split('/');
    const routeKey = '/' + routeSplit.slice(1, 4).join('/');
    routeSet.add(routeKey);
  });
  const authorizedNavItems = Array.from(routeSet).reduce<
    typeof VERTICAL_NAV_ITEMS
  >((acc, r) => {
    if (VERTICAL_NAV_ITEMS[r]) {
      acc[r] = VERTICAL_NAV_ITEMS[r];
    }
    return acc;
  }, {});
  return authorizedNavItems;
};
